import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Share2, Play, Clock, Calendar, Tag } from 'lucide-react';
import { toast } from 'react-toastify';

import { Clip } from '../types';
import CustomVideoPlayer from '../components/CustomVideoPlayer';
import ClipDescription from '../components/ClipDescription';

interface PublicClipData {
  id: string;
  title: string;
  description: string;
  duration: number;
  file_url: string;
  thumbnail_url?: string;
  created_at: string;
  tags: string[];
  is_public?: boolean;
  is_shared?: boolean;
  share_token?: string;
}

const PublicClip: React.FC = () => {
  const { clipId, shareToken } = useParams<{ clipId?: string; shareToken?: string }>();
  const [clipData, setClipData] = useState<PublicClipData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchClipData = async () => {
      try {
        setLoading(true);
        let response;
        
        if (shareToken) {
          // Fetch by share token
          response = await fetch(`/api/v1/clips/share/${shareToken}`);
        } else if (clipId) {
          // Fetch public clip
          response = await fetch(`/api/v1/clips/public/${clipId}`);
        } else {
          throw new Error('No clip ID or share token provided');
        }

        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('Clip not found or link expired');
          } else if (response.status === 403) {
            throw new Error('This clip is not publicly accessible');
          } else {
            throw new Error('Failed to load clip');
          }
        }

        const data = await response.json();
        setClipData(data);
      } catch (err: any) {
        setError(err.message || 'Failed to load clip');
        toast.error(err.message || 'Failed to load clip');
      } finally {
        setLoading(false);
      }
    };

    fetchClipData();
  }, [clipId, shareToken]);

  const handleShare = async () => {
    if (!clipData) return;
    
    const shareData = {
      title: clipData.title,
      text: clipData.description,
      url: window.location.href
    };
    
    try {
      // Try Web Share API first
      if (navigator.share) {
        await navigator.share(shareData);
        toast.success('Shared successfully!');
      } else {
        // Fallback to clipboard
        await navigator.clipboard.writeText(window.location.href);
        toast.success('Link copied to clipboard!');
      }
    } catch (error) {
      console.error('Share error:', error);
      // Manual fallback
      const textArea = document.createElement('textarea');
      textArea.value = window.location.href;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast.success('Link copied to clipboard!');
    }
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return 'Unknown date';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-red-500 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Loading clip...</h2>
          <p className="text-gray-600">Please wait while we load the shared content</p>
        </div>
      </div>
    );
  }

  if (error || !clipData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="bg-red-100 rounded-full p-4 w-20 h-20 mx-auto mb-4 flex items-center justify-center">
            <Play className="h-10 w-10 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Content Not Available</h2>
          <p className="text-gray-600 mb-6">
            {error || 'The shared clip could not be found or may have been removed.'}
          </p>
          <Link
            to="/"
                            className="inline-flex items-center px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Go to Homepage
          </Link>
        </div>
      </div>
    );
  }

  // Convert to Clip format for the video player
  const clip: Clip = {
    id: clipData.id,
    video_id: '',
    user_id: '',
    highlight_id: '',
    start_time: 0,
    end_time: clipData.duration,
    duration: clipData.duration,
    file_path: '',
    file_url: clipData.file_url,
    thumbnail_url: clipData.thumbnail_url,
    title: clipData.title,
    description: clipData.description,
    tags: clipData.tags,
    created_at: clipData.created_at,
    export_formats: {} as any
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link
              to="/"
              className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              ClipThis
            </Link>
            
            <div className="flex items-center space-x-3">
              <button
                onClick={handleShare}
                className="flex items-center px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Share2 className="h-4 w-4 mr-2" />
                Share
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="space-y-6">
          {/* Video Player */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="aspect-video">
              <CustomVideoPlayer
                clip={clip}
                className="w-full h-full"
              />
            </div>
          </div>

          {/* Clip Information */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="mb-4">
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                {clipData.title}
              </h1>
              {clipData.description && (
                <ClipDescription description={clipData.description} />
              )}
            </div>

            {/* Metadata */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
              <div className="flex items-center">
                <Clock className="h-4 w-4 mr-1" />
                {formatDuration(clipData.duration)}
              </div>
              <div className="flex items-center">
                <Calendar className="h-4 w-4 mr-1" />
                {formatDate(clipData.created_at)}
              </div>
              {clipData.is_shared && (
                <div className="flex items-center">
                  <Share2 className="h-4 w-4 mr-1" />
                  Shared via private link
                </div>
              )}
              {clipData.is_public && (
                <div className="flex items-center">
                  <Share2 className="h-4 w-4 mr-1" />
                  Public clip
                </div>
              )}
            </div>

            {/* Tags */}
            {clipData.tags && clipData.tags.length > 0 && (
              <div className="mt-4">
                <div className="flex items-center mb-2">
                  <Tag className="h-4 w-4 mr-2 text-gray-400" />
                  <span className="text-sm font-medium text-gray-700">Tags</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {clipData.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm font-medium"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Call to Action */}
          <div className="bg-gradient-to-r from-black to-gray-900 rounded-xl shadow-lg p-6 text-white text-center">
            <h3 className="text-xl font-bold mb-2">Create Your Own Clips</h3>
            <p className="mb-4 opacity-90">
              Turn your long videos into engaging clips with AI-powered highlights
            </p>
            <Link
              to="/"
                              className="inline-flex items-center px-6 py-3 bg-white text-black font-semibold rounded-lg hover:bg-gray-100 transition-colors"
            >
              Try ClipThis Free
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicClip; 